// src/modulos/Parametros/ContactReasonSACPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const CONTACT_REASON_COLLECTION = 'parameters/data/motivo-contato-sac';
const ITEMS_PER_PAGE = 10;

// Definição dos tipos de motivo
const REASON_TYPES = {
    COMPLAINT: { value: 'complaint', label: '📢 Reclamação', color: '#dc3545', badgeStyle: 'danger' },
    PRAISE: { value: 'praise', label: '🌟 Elogio', color: '#28a745', badgeStyle: 'success' },
    DOUBT: { value: 'doubt', label: '❓ Dúvida', color: '#17a2b8', badgeStyle: 'info' },
    SUGGESTION: { value: 'suggestion', label: '💡 Sugestão', color: '#ffc107', badgeStyle: 'warning' },
    SUPPORT: { value: 'support', label: '🛠️ Suporte Técnico', color: '#6f42c1', badgeStyle: 'purple' },
    OTHER: { value: 'other', label: '📋 Outros', color: '#6c757d', badgeStyle: 'secondary' }
};

const ContactReasonSACPage = () => {
    const can = usePagePermissions('motivo_de_contato_sac');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [typeInput, setTypeInput] = useState(REASON_TYPES.OTHER.value);
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [isCheckingUsage, setIsCheckingUsage] = useState(false);

    // Hook de Dados
    const {
        data: reasons,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        checkDocumentUsage,
        refreshData
    } = useFirestore(CONTACT_REASON_COLLECTION, 'name');

    // Filtros combinados
    const filteredItems = useMemo(() => {
        if (!reasons) return [];

        return reasons.filter(item => {
            const matchesSearch = !searchTerm ||
                item.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesType = typeFilter === 'all' || item.type === typeFilter;
            return matchesSearch && matchesType;
        });
    }, [reasons, searchTerm, typeFilter]);

    const totalFilteredItems = filteredItems.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentItems = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredItems, currentPage]);

    // Helper para obter estilo do badge
    const getBadgeStyle = useCallback((type) => {
        const typeConfig = Object.values(REASON_TYPES).find(t => t.value === type);
        return typeConfig?.badgeStyle || 'secondary';
    }, []);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setTypeInput(REASON_TYPES.OTHER.value);
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name || '');
        setTypeInput(item.type || REASON_TYPES.OTHER.value);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback(async (id, name) => {
        setIsCheckingUsage(true);
        const usageCheck = await checkDocumentUsage(id);
        setIsCheckingUsage(false);

        if (usageCheck.isUsed) {
            setFormError(`Não é possível excluir "${name}" pois está sendo utilizado em ${usageCheck.count} atendimento(s).`);
            setTimeout(() => setFormError(''), 5000);
            return;
        }

        setDeleteConfirm({ isOpen: true, id, name });
    }, [checkDocumentUsage]);

    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Motivo "${name}" deletado com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const remainingItems = filteredItems.length - 1;
                const maxPage = Math.ceil(remainingItems / ITEMS_PER_PAGE);
                if (currentPage > maxPage && maxPage > 0) {
                    setCurrentPage(maxPage);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError(`Erro ao deletar: ${result.error}`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar motivo. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, filteredItems.length, currentPage]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O motivo de contato é obrigatório.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            type: typeInput,
            ...timestamps,
            ...(!editingItem && { isActive: true })
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Motivo editado com sucesso!' : 'Motivo adicionado com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, typeInput, editingItem, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    const handleTypeFilterChange = useCallback((e) => {
        setTypeFilter(e.target.value);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Novo Motivo de Contato'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Motivo'}
            >
                <div className={styles.formRow}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="type">
                            Tipo do Motivo <span className={styles.required}>*</span>
                        </label>
                        <select
                            id="type"
                            value={typeInput}
                            onChange={(e) => setTypeInput(e.target.value)}
                            className={styles.select}
                            disabled={formLoading}
                            required
                        >
                            {Object.values(REASON_TYPES).map(type => (
                                <option key={type.value} value={type.value}>
                                    {type.label}
                                </option>
                            ))}
                        </select>
                        <small className={styles.fieldHint}>
                            Categorize corretamente para gerar métricas precisas
                        </small>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="name">
                            Nome do Motivo <span className={styles.required}>*</span>
                        </label>
                        <input
                            id="name"
                            type="text"
                            value={nameInput}
                            onChange={(e) => setNameInput(e.target.value)}
                            placeholder="Ex: Produto com defeito, Atendimento lento..."
                            required
                            className={styles.input}
                            disabled={formLoading}
                        />
                    </div>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar o motivo "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header */}
            <PageHeader
                title="Motivos de Contato (SAC)"
                actionText="Novo Motivo"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar with Type Filter */}
            <div className={styles.searchBar}>
                <div style={{ display: 'flex', gap: '120px', flexWrap: 'wrap' }}>
                    <SearchBar
                        placeholder="🔍 Buscar por nome..."
                        onSearch={handleSearch}
                        disabled={listLoading || formLoading}
                    />

                    <select
                        value={typeFilter}
                        onChange={handleTypeFilterChange}
                        className={styles.select}
                        style={{ width: '200px' }}
                        disabled={listLoading || formLoading}
                    >
                        <option value="all">📋 Todos os tipos</option>
                        {Object.values(REASON_TYPES).map(type => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar dados: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={4} rows={ITEMS_PER_PAGE} />
                    ) : currentItems.length === 0 ? (
                        <EmptyState
                            icon={searchTerm || typeFilter !== 'all' ? '🔍' : '📭'}
                            title={
                                searchTerm || typeFilter !== 'all'
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhum motivo cadastrado'
                            }
                            message={
                                searchTerm || typeFilter !== 'all'
                                    ? 'Tente ajustar os filtros de busca'
                                    : "Clique em '+ Novo Motivo' para começar"
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Motivo de Contato</th>
                                            <th style={{ width: '180px' }}>Tipo</th>
                                            <th style={{ width: '160px' }}>Última atualização</th>
                                            <th style={{ width: '160px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    <span className={styles.rowMetadata}>
                                                        Criado: {item.createdAt ? new Date(item.createdAt).toLocaleDateString('pt-BR') : 'N/A'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className={`${styles.badge} ${styles[getBadgeStyle(item.type)]}`}>
                                                        {Object.values(REASON_TYPES).find(t => t.value === item.type)?.label || item.type}
                                                    </span>
                                                </td>
                                                <td>
                                                    {item.updatedAt
                                                        ? new Date(item.updatedAt).toLocaleString('pt-BR')
                                                        : 'Não informado'}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading || isCheckingUsage}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading || isCheckingUsage}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="motivos"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default ContactReasonSACPage;